import { parseAsStringEnum, useQueryState } from 'nuqs'
import { useEffect, useRef } from 'react'
import { FocusScope, useKeyboard, usePreventScroll } from 'react-aria'
import { twJoin } from 'tailwind-merge'

import { useHeaderContext } from '@/lib/provider/HeaderContext'
import ShelfSourceLine from '@components/shelfDetails/ShelfSourceLine'
import ShelfWrapper from '@components/shelfDetails/ShelfWrapper'
import SingleShelfView from '@components/shelfDetails/SingleShelfView'
import TripleShelfView from '@components/shelfDetails/TripleShelfView'
import { ALL_VIEW_TYPES, type ViewType } from '@components/shelfDetails/utils'
import { useIsWideLayout } from '@hooks/useMediaQuery'
import { useShelf } from '@state/shelf/queries'

import type { ProductWrapper } from '@state/product.ts'
import type { TransformedFacing } from '@state/shelf/types'

const DIALOG_TITLE_ID = 'shelf-details-title'

type ModalMode = 'docked' | 'expanded'

export interface FacingProducerOptions {
  viewType: ViewType
  facing: TransformedFacing
  product?: ProductWrapper
  state: 'default' | 'hovered' | 'dimmed'
}

export type FacingProducer = (options: FacingProducerOptions) => string

interface ShelfDetailsProps {
  shelfId?: string
  onShelfIdChange?: (shelfId?: string) => void
  initialViewType?: ViewType
  initialModalMode?: ModalMode
  facingProducer?: FacingProducer
}

function ShelfDetails({
  shelfId,
  onShelfIdChange,
  initialViewType = 'product',
  initialModalMode = 'docked',
  facingProducer,
}: ShelfDetailsProps) {
  // Internal state management for view type and modal mode
  const [viewType, setViewType] = useQueryState<ViewType>(
    'shelf-view',
    parseAsStringEnum([...ALL_VIEW_TYPES]).withDefault(initialViewType)
  )
  const [modalMode, setModalMode] = useQueryState<ModalMode>(
    'shelf-modal',
    parseAsStringEnum(['docked', 'expanded']).withDefault(initialModalMode)
  )
  const { setShowQuickSelection } = useHeaderContext()

  const { data: shelf, error, isLoading } = useShelf(shelfId)

  const isOpen = !!shelfId
  const isWideLayout = useIsWideLayout()
  const dialogRef = useRef<HTMLDivElement>(null)

  const isExpanded = !isWideLayout || modalMode === 'expanded'
  const showBackdrop = isExpanded

  // Prevent body scroll when modal is expanded
  usePreventScroll({ isDisabled: !isExpanded || !isOpen })

  useEffect(() => {
    setShowQuickSelection(!isOpen)
  }, [isOpen, setShowQuickSelection])

  function handleClose() {
    setModalMode(initialModalMode)
    setViewType(initialViewType)
    onShelfIdChange?.(undefined)
  }

  function switchToLeftNeighbour() {
    if (!shelf?.leftNeighborShelfId) return
    onShelfIdChange?.(shelf.leftNeighborShelfId)
  }

  function switchToRightNeighbour() {
    if (!shelf?.rightNeighborShelfId) return
    onShelfIdChange?.(shelf.rightNeighborShelfId)
  }

  function toggleModal() {
    setModalMode(modalMode === 'docked' ? 'expanded' : 'docked')
  }

  const { keyboardProps } = useKeyboard({
    onKeyDown: (e) => {
      if (e.key === 'Escape') {
        handleClose()
        return
      }

      if (e.key === 'ArrowLeft' && isExpanded) {
        switchToLeftNeighbour()
        return
      }

      if (e.key === 'ArrowRight' && isExpanded) {
        switchToRightNeighbour()
        return
      }
    },
    isDisabled: !isOpen,
  })

  if (!isOpen || !shelfId) {
    return null
  }

  const backdropClasses = twJoin(
    'fixed inset-0 z-60 transition-all duration-300', // z-index higher than navbar (50)
    showBackdrop ? 'bg-black/40 backdrop-blur-sm' : 'pointer-events-none bg-transparent'
  )

  const containerClasses = twJoin(
    'fixed z-70 bg-white shadow-2xl transition-all duration-300 ease-in-out',
    !isWideLayout && 'inset-0', // Mobile: always full-screen, no border radius, no gaps
    isWideLayout && 'rounded-xl border border-secondary', // desktop styles
    isWideLayout && modalMode === 'docked' && 'top-6 right-6 h-[calc(100%-48px)] w-[480px]', // Desktop Docked: Right sidebar with gaps
    isWideLayout && modalMode === 'expanded' && 'inset-6' // Desktop Expanded: Full screen overlay with gaps
  )

  return (
    <>
      {/* Backdrop - only blocks clicks when expanded */}
      <div
        className={backdropClasses}
        onClick={showBackdrop ? handleClose : undefined}
        aria-hidden={!showBackdrop}
      />

      {/* Modal content */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal={isExpanded}
        aria-labelledby={DIALOG_TITLE_ID}
        className={containerClasses}
        {...keyboardProps}
      >
        {isLoading && <ShelfWrapper.Skeleton/>}

        {error && <ShelfWrapper.ErrorMsg error={error} onClose={handleClose} />}

        {shelf &&
              <FocusScope contain={isExpanded} restoreFocus autoFocus> { /* Trap focus when expanded */ }
                <ShelfWrapper
                  titleId={DIALOG_TITLE_ID}
                  shelfName={shelf.name}
                  type={viewType}
                  onViewTypeChange={setViewType}
                  isExpanded={isExpanded}
                  onExpandCollapse={toggleModal}
                  onClose={handleClose}
                  sourceLine={<ShelfSourceLine shelf={shelf} />}
                >
                  { isExpanded && isWideLayout ?
                    <TripleShelfView
                      type={viewType}
                      shelf={shelf}
                      onLeftNeighbourClick={switchToLeftNeighbour}
                      onRightNeighbourClick={switchToRightNeighbour}
                      facingProducer={facingProducer}
                    />
                    :
                    <SingleShelfView
                      type={viewType}
                      shelf={shelf}
                      facingProducer={facingProducer}
                    />
                  }
                </ShelfWrapper>
              </FocusScope>
        }
      </div>
    </>
  )
}

export default ShelfDetails
